// src/admin/components/WarehouseDrawer.tsx
import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Drawer,
    TextField,
    Typography,
    IconButton,
    Alert,
    CircularProgress,
    Grid,
    FormControlLabel,
    Switch,
    Divider,
    Card,
    CardContent,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    InputAdornment,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Backdrop,
    LinearProgress,
} from '@mui/material';
import {
    Close as CloseIcon,
    LocationOn as LocationIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    Person as PersonIcon,
    Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { Warehouse, WarehouseAddress, WarehouseContact, WarehouseFormData, WarehouseOperatingHours, WeekDay, WEEK_DAYS, DayHours, createDefaultWeekHours } from './warehouse';
import { WarehouseService } from './warehouseService';
import { useAuth } from '../../../contexts/AuthContext';
import { WarehouseCountryService, CountryOption, StateOption } from './warehouseCountryService';

interface WarehouseDrawerProps {
    isOpen: boolean;
    mode: 'add' | 'edit' | 'view';
    warehouse: Warehouse | null;
    onClose: () => void;
    onSave: () => void;
    onShowToast: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

const WarehouseDrawer: React.FC<WarehouseDrawerProps> = ({
    isOpen,
    mode,
    warehouse,
    onClose,
    onSave,
    onShowToast,
}) => {
    const { currentUser } = useAuth();
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [countriesLoading, setCountriesLoading] = useState(false);
    const [availableCountries, setAvailableCountries] = useState<CountryOption[]>([]);
    const [availableStates, setAvailableStates] = useState<StateOption[]>([]);

    const [formData, setFormData] = useState<WarehouseFormData>({
        name: '',
        code: '',
        description: '',
        address: {
            street: '',
            city: '',
            state: '',
            country: '',
            zipCode: '',
        },
        contact: {
            phone: '',
            email: '',
            manager: '',
        },
        capacity: {
            total: '',
            used: '0',
        },
        isActive: true,
        displayInWebsite: false,
        operatingHours: {
            open: '09:00',
            close: '18:00',
            timezone: 'UTC',
            days: createDefaultWeekHours('09:00', '18:00'),
        },
    });

    // Load countries when drawer opens
    useEffect(() => {
        if (isOpen) {
            loadCountries();
        }
    }, [isOpen]);

    // Load countries from shipping service
    const loadCountries = async () => {
        try {
            setCountriesLoading(true);
            const countries = await WarehouseCountryService.getAllCountries();
            setAvailableCountries(countries);
        } catch (err: any) {
            console.error('Error loading countries:', err);
            onShowToast('Failed to load countries', 'error');
        } finally {
            setCountriesLoading(false);
        }
    };

    // Update available states when country changes
    useEffect(() => {
        const loadStates = async () => {
            if (formData.address.country) {
                try {
                    const states = await WarehouseCountryService.getStatesByCountryName(formData.address.country);
                    setAvailableStates(states);

                    // Reset state if the current state is not available in the new country
                    if (formData.address.state && !states.find(state => state.name === formData.address.state)) {
                        setFormData(prev => ({
                            ...prev,
                            address: {
                                ...prev.address,
                                state: ''
                            }
                        }));
                    }
                } catch (err: any) {
                    console.error('Error loading states:', err);
                    setAvailableStates([]);
                }
            } else {
                setAvailableStates([]);
            }
        };

        loadStates();
    }, [formData.address.country]);

    useEffect(() => {
        if (isOpen && warehouse && (mode === 'edit' || mode === 'view')) {
            const newFormData = {
                name: warehouse.name,
                code: warehouse.code,
                description: warehouse.description,
                address: warehouse.address,
                contact: warehouse.contact,
                capacity: {
                    total: warehouse.capacity.total.toString(),
                    used: warehouse.capacity.used.toString(),
                },
                isActive: warehouse.isActive,
                displayInWebsite: warehouse.displayInWebsite ?? false,
                operatingHours: warehouse.operatingHours
                    ? {
                        open: warehouse.operatingHours.open || '09:00',
                        close: warehouse.operatingHours.close || '18:00',
                        timezone: warehouse.operatingHours.timezone || 'UTC',
                        days: warehouse.operatingHours.days || createDefaultWeekHours(
                            warehouse.operatingHours.open || '09:00',
                            warehouse.operatingHours.close || '18:00'
                        ),
                    }
                    : {
                        open: '09:00',
                        close: '18:00',
                        timezone: 'UTC',
                        days: createDefaultWeekHours('09:00', '18:00'),
                    },
            };

            setFormData(newFormData);
        } else if (isOpen && mode === 'add') {
            resetForm();
        }
        setError('');
        setActiveStep(0);
    }, [isOpen, warehouse, mode]);

    const resetForm = () => {
        setFormData({
            name: '',
            code: '',
            description: '',
            address: {
                street: '',
                city: '',
                state: '',
                country: '',
                zipCode: '',
            },
            contact: {
                phone: '',
                email: '',
                manager: '',
            },
            capacity: {
                total: '',
                used: '0',
            },
            isActive: true,
            displayInWebsite: false,
            operatingHours: {
                open: '09:00',
                close: '18:00',
                timezone: 'UTC',
                days: createDefaultWeekHours('09:00', '18:00'),
            },
        });
        setAvailableStates([]);
    };

    // Fixed handleInputChange function
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;

        if (name.startsWith('address.')) {
            const field = name.replace('address.', '') as keyof WarehouseAddress;
            setFormData(prev => ({
                ...prev,
                address: {
                    ...prev.address,
                    [field]: value
                }
            }));
        } else if (name.startsWith('contact.')) {
            const field = name.replace('contact.', '') as keyof WarehouseContact;
            setFormData(prev => ({
                ...prev,
                contact: {
                    ...prev.contact,
                    [field]: value
                }
            }));
        } else if (name.startsWith('capacity.')) {
            const field = name.replace('capacity.', '') as 'total' | 'used';
            setFormData(prev => ({
                ...prev,
                capacity: {
                    ...prev.capacity,
                    [field]: value
                }
            }));
        } else if (name.startsWith('operatingHours.')) {
            const field = name.replace('operatingHours.', '') as keyof WarehouseOperatingHours;
            setFormData(prev => ({
                ...prev,
                operatingHours: {
                    ...prev.operatingHours,
                    [field]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
            }));
        }
    };

    // Handle select changes for country and state
    const handleSelectChange = (e: any) => {
        const { name, value } = e.target;

        if (name === 'address.country') {
            setFormData(prev => ({
                ...prev,
                address: {
                    ...prev.address,
                    country: value,
                    state: '' // Reset state when country changes
                }
            }));
        } else if (name === 'address.state') {
            setFormData(prev => ({
                ...prev,
                address: {
                    ...prev.address,
                    state: value
                }
            }));
        }
    };

    // Update a single day's opening hours (isOpen / open / close)
    const handleDayHoursChange = (day: WeekDay, field: keyof DayHours, value: string | boolean) => {
        setFormData(prev => {
            const currentDays = prev.operatingHours.days || createDefaultWeekHours(
                prev.operatingHours.open,
                prev.operatingHours.close
            );
            return {
                ...prev,
                operatingHours: {
                    ...prev.operatingHours,
                    days: {
                        ...currentDays,
                        [day]: {
                            ...currentDays[day],
                            [field]: value,
                        },
                    },
                },
            };
        });
    };

    const dayLabels: Record<WeekDay, string> = {
        monday: 'Monday',
        tuesday: 'Tuesday',
        wednesday: 'Wednesday',
        thursday: 'Thursday',
        friday: 'Friday',
        saturday: 'Saturday',
        sunday: 'Sunday',
    };

    const validateForm = (): boolean => {
        if (!formData.name.trim()) {
            setError('Warehouse name is required');
            onShowToast('Warehouse name is required', 'error');
            return false;
        }

        if (!formData.code.trim()) {
            setError('Warehouse code is required');
            onShowToast('Warehouse code is required', 'error');
            return false;
        }

        if (formData.code.length < 2 || formData.code.length > 10) {
            setError('Warehouse code must be between 2 and 10 characters');
            onShowToast('Warehouse code must be between 2 and 10 characters', 'error');
            return false;
        }

        if (!formData.address.street.trim()) {
            setError('Street address is required');
            onShowToast('Street address is required', 'error');
            return false;
        }

        if (!formData.address.city.trim()) {
            setError('City is required');
            onShowToast('City is required', 'error');
            return false;
        }

        if (!formData.address.country.trim()) {
            setError('Country is required');
            onShowToast('Country is required', 'error');
            return false;
        }

        if (!formData.address.state.trim()) {
            setError('State/Province is required');
            onShowToast('State/Province is required', 'error');
            return false;
        }

        if (!formData.contact.manager.trim()) {
            setError('Manager name is required');
            onShowToast('Manager name is required', 'error');
            return false;
        }

        if (!formData.capacity.total || parseFloat(formData.capacity.total) <= 0) {
            setError('Total capacity must be greater than 0');
            onShowToast('Total capacity must be greater than 0', 'error');
            return false;
        }

        const used = parseFloat(formData.capacity.used) || 0;
        const total = parseFloat(formData.capacity.total) || 0;
        if (used > total) {
            setError('Used capacity cannot exceed total capacity');
            onShowToast('Used capacity cannot exceed total capacity', 'error');
            return false;
        }

        return true;
    };

    const getUserName = () => {
        if (!currentUser) return 'Unknown User';

        // Adjust these based on your user object structure
        return currentUser.displayName || currentUser.email ||
            'Unknown User';
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (mode === 'view') return;

        if (!validateForm()) return;

        try {
            setLoading(true);
            setError('');

            const userId = currentUser?.uid || 'unknown';
            const userName = getUserName();

            if (mode === 'add') {
                await WarehouseService.createWarehouse(formData, userId, userName);
                onShowToast('Warehouse created successfully', 'success');
            } else if (mode === 'edit' && warehouse?.id) {
                await WarehouseService.updateWarehouse(warehouse.id, formData, userId, userName);
                onShowToast('Warehouse updated successfully', 'success');
            }

            onSave();
        } catch (err: any) {
            console.error('Error saving warehouse:', err);
            setError(err.message || 'Failed to save warehouse');
            onShowToast(err.message || 'Failed to save warehouse', 'error');
        } finally {
            setLoading(false);
        }
    };

    const getTitle = () => {
        switch (mode) {
            case 'add':
                return 'Add New Warehouse';
            case 'edit':
                return 'Edit Warehouse';
            case 'view':
                return 'Warehouse Details';
            default:
                return '';
        }
    };

    const steps = ['Basic Information', 'Address & Contact', 'Capacity & Hours'];

    if (!isOpen) return null;

    return (
        <>
            <Drawer
                anchor="right"
                open={isOpen}
                onClose={onClose}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: '100%', md: 700 },
                        maxWidth: 700
                    }
                }}
            >
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* Header */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        borderBottom: 1,
                        borderColor: 'divider'
                    }}>
                        <Typography variant="h6">{getTitle()}</Typography>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
                            {error && (
                                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                                    {error}
                                </Alert>
                            )}

                            {countriesLoading && (
                                <Alert severity="info" sx={{ mb: 3 }}>
                                    Loading countries and states...
                                </Alert>
                            )}

                            {mode !== 'view' ? (
                                <Stepper activeStep={activeStep} orientation="vertical" sx={{ mb: 3 }}>
                                    {/* Step 1: Basic Information */}
                                    <Step>
                                        <StepLabel>Basic Information</StepLabel>
                                        <StepContent>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <TextField
                                                        fullWidth
                                                        label="Warehouse Name *"
                                                        name="name"
                                                        value={formData.name}
                                                        onChange={handleInputChange}
                                                        placeholder="Enter warehouse name"
                                                    />
                                                </Grid>

                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Warehouse Code *"
                                                        name="code"
                                                        value={formData.code}
                                                        onChange={handleInputChange}
                                                        placeholder="e.g., WH-001"
                                                        inputProps={{
                                                            style: { textTransform: 'uppercase' },
                                                            maxLength: 10
                                                        }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} sm={6}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                name="isActive"
                                                                checked={formData.isActive}
                                                                onChange={handleInputChange}
                                                                color="primary"
                                                            />
                                                        }
                                                        label="Active Warehouse"
                                                    />
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                name="displayInWebsite"
                                                                checked={formData.displayInWebsite}
                                                                onChange={handleInputChange}
                                                                color="primary"
                                                            />
                                                        }
                                                        label="Display in website (show as a branch on the storefront)"
                                                    />
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6 }}>
                                                        When enabled, this warehouse appears in the "Branches" section of the customer website.
                                                    </Typography>
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <TextField
                                                        fullWidth
                                                        label="Description"
                                                        name="description"
                                                        value={formData.description}
                                                        onChange={handleInputChange}
                                                        multiline
                                                        rows={3}
                                                        placeholder="Enter warehouse description"
                                                    />
                                                </Grid>
                                            </Grid>

                                            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                                <Button
                                                    onClick={() => setActiveStep(1)}
                                                    variant="contained"
                                                    disabled={!formData.name.trim() || !formData.code.trim()}
                                                >
                                                    Continue to Address
                                                </Button>
                                            </Box>
                                        </StepContent>
                                    </Step>

                                    {/* Step 2: Address & Contact */}
                                    <Step>
                                        <StepLabel>Address & Contact</StepLabel>
                                        <StepContent>
                                            <Card variant="outlined" sx={{ mb: 2 }}>
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <LocationIcon sx={{ mr: 1 }} />
                                                        Address Information
                                                    </Typography>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12}>
                                                            <TextField
                                                                fullWidth
                                                                label="Street Address *"
                                                                name="address.street"
                                                                value={formData.address.street}
                                                                onChange={handleInputChange}
                                                                placeholder="Enter street address"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="City *"
                                                                name="address.city"
                                                                value={formData.address.city}
                                                                onChange={handleInputChange}
                                                                placeholder="Enter city"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <FormControl fullWidth disabled={countriesLoading}>
                                                                <InputLabel>Country *</InputLabel>
                                                                <Select
                                                                    name="address.country"
                                                                    value={formData.address.country}
                                                                    onChange={handleSelectChange}
                                                                    label="Country *"
                                                                >
                                                                    <MenuItem value="">
                                                                        <em>Select Country</em>
                                                                    </MenuItem>
                                                                    {availableCountries.map((country) => (
                                                                        <MenuItem key={country.code} value={country.name}>
                                                                            {country.name}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <FormControl fullWidth disabled={!formData.address.country}>
                                                                <InputLabel>State/Province *</InputLabel>
                                                                <Select
                                                                    name="address.state"
                                                                    value={formData.address.state}
                                                                    onChange={handleSelectChange}
                                                                    label="State/Province *"
                                                                >
                                                                    <MenuItem value="">
                                                                        <em>Select State/Province</em>
                                                                    </MenuItem>
                                                                    {availableStates.map((state) => (
                                                                        <MenuItem key={state.id} value={state.name}>
                                                                            {state.name}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="ZIP/Postal Code"
                                                                name="address.zipCode"
                                                                value={formData.address.zipCode}
                                                                onChange={handleInputChange}
                                                                placeholder="Enter ZIP code"
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </CardContent>
                                            </Card>

                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <PersonIcon sx={{ mr: 1 }} />
                                                        Contact Information
                                                    </Typography>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="Manager Name *"
                                                                name="contact.manager"
                                                                value={formData.contact.manager}
                                                                onChange={handleInputChange}
                                                                placeholder="Enter manager name"
                                                                InputProps={{
                                                                    startAdornment: (
                                                                        <InputAdornment position="start">
                                                                            <PersonIcon />
                                                                        </InputAdornment>
                                                                    ),
                                                                }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="Phone Number"
                                                                name="contact.phone"
                                                                value={formData.contact.phone}
                                                                onChange={handleInputChange}
                                                                placeholder="Enter phone number"
                                                                InputProps={{
                                                                    startAdornment: (
                                                                        <InputAdornment position="start">
                                                                            <PhoneIcon />
                                                                        </InputAdornment>
                                                                    ),
                                                                }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <TextField
                                                                fullWidth
                                                                label="Email Address"
                                                                name="contact.email"
                                                                type="email"
                                                                value={formData.contact.email}
                                                                onChange={handleInputChange}
                                                                placeholder="Enter email address"
                                                                InputProps={{
                                                                    startAdornment: (
                                                                        <InputAdornment position="start">
                                                                            <EmailIcon />
                                                                        </InputAdornment>
                                                                    ),
                                                                }}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </CardContent>
                                            </Card>

                                            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                                <Button onClick={() => setActiveStep(0)} variant="outlined">
                                                    Back
                                                </Button>
                                                <Button
                                                    onClick={() => setActiveStep(2)}
                                                    variant="contained"
                                                    disabled={!formData.address.street.trim() || !formData.address.city.trim() || !formData.address.country || !formData.address.state || !formData.contact.manager.trim()}
                                                >
                                                    Continue to Capacity
                                                </Button>
                                            </Box>
                                        </StepContent>
                                    </Step>

                                    {/* Step 3: Capacity & Hours */}
                                    <Step>
                                        <StepLabel>Capacity & Hours</StepLabel>
                                        <StepContent>
                                            <Card variant="outlined" sx={{ mb: 2 }}>
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom>
                                                        Capacity Information
                                                    </Typography>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="Total Capacity *"
                                                                name="capacity.total"
                                                                type="number"
                                                                value={formData.capacity.total}
                                                                onChange={handleInputChange}
                                                                placeholder="0"
                                                                inputProps={{ min: 0, step: "0.01" }}
                                                                helperText="Total storage capacity"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="Used Capacity"
                                                                name="capacity.used"
                                                                type="number"
                                                                value={formData.capacity.used}
                                                                onChange={handleInputChange}
                                                                placeholder="0"
                                                                inputProps={{ min: 0, step: "0.01" }}
                                                                helperText="Currently used capacity"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Available Capacity: {(
                                                                        parseFloat(formData.capacity.total || '0') -
                                                                        parseFloat(formData.capacity.used || '0')
                                                                    ).toLocaleString()} units
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                </CardContent>
                                            </Card>

                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <ScheduleIcon sx={{ mr: 1 }} />
                                                        Operating Hours
                                                    </Typography>
                                                    <Grid container spacing={2} sx={{ mb: 1 }}>
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                fullWidth
                                                                label="Timezone"
                                                                name="operatingHours.timezone"
                                                                value={formData.operatingHours.timezone}
                                                                onChange={handleInputChange}
                                                                placeholder="e.g., UTC"
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                    <Divider sx={{ my: 1.5 }} />
                                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                        Opening days &amp; times
                                                    </Typography>
                                                    {WEEK_DAYS.map((day) => {
                                                        const dayHours = (formData.operatingHours.days || createDefaultWeekHours(
                                                            formData.operatingHours.open,
                                                            formData.operatingHours.close
                                                        ))[day];
                                                        return (
                                                            <Grid
                                                                container
                                                                spacing={2}
                                                                alignItems="center"
                                                                key={day}
                                                                sx={{ mb: 1 }}
                                                            >
                                                                <Grid item xs={12} sm={4}>
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Switch
                                                                                checked={dayHours.isOpen}
                                                                                onChange={(e) => handleDayHoursChange(day, 'isOpen', e.target.checked)}
                                                                                color="primary"
                                                                            />
                                                                        }
                                                                        label={dayLabels[day]}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={6} sm={4}>
                                                                    <TextField
                                                                        fullWidth
                                                                        size="small"
                                                                        label="Open"
                                                                        type="time"
                                                                        value={dayHours.open}
                                                                        onChange={(e) => handleDayHoursChange(day, 'open', e.target.value)}
                                                                        disabled={!dayHours.isOpen}
                                                                        InputLabelProps={{ shrink: true }}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={6} sm={4}>
                                                                    <TextField
                                                                        fullWidth
                                                                        size="small"
                                                                        label="Close"
                                                                        type="time"
                                                                        value={dayHours.close}
                                                                        onChange={(e) => handleDayHoursChange(day, 'close', e.target.value)}
                                                                        disabled={!dayHours.isOpen}
                                                                        InputLabelProps={{ shrink: true }}
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        );
                                                    })}
                                                </CardContent>
                                            </Card>

                                            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                                <Button onClick={() => setActiveStep(1)} variant="outlined">
                                                    Back
                                                </Button>
                                                <Button
                                                    onClick={handleSubmit}
                                                    variant="contained"
                                                    disabled={loading || !formData.capacity.total}
                                                    startIcon={loading && <CircularProgress size={16} />}
                                                >
                                                    {mode === 'add' ? 'Create Warehouse' : 'Update Warehouse'}
                                                </Button>
                                            </Box>
                                        </StepContent>
                                    </Step>
                                </Stepper>
                            ) : (
                                // View Mode
                                <Box>
                                    {warehouse && (
                                        <Grid container spacing={3}>
                                            {/* Basic Information */}
                                            <Grid item xs={12}>
                                                <Card>
                                                    <CardContent>
                                                        <Typography variant="h6" gutterBottom>
                                                            Basic Information
                                                        </Typography>
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Name
                                                                </Typography>
                                                                <Typography variant="body1" gutterBottom>
                                                                    {warehouse.name}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Code
                                                                </Typography>
                                                                <Typography variant="body1" gutterBottom>
                                                                    {warehouse.code}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Description
                                                                </Typography>
                                                                <Typography variant="body1" gutterBottom>
                                                                    {warehouse.description || 'No description provided'}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Status
                                                                </Typography>
                                                                <Typography
                                                                    variant="body1"
                                                                    color={warehouse.isActive ? 'success.main' : 'error.main'}
                                                                    sx={{ fontWeight: 600 }}
                                                                >
                                                                    {warehouse.isActive ? 'Active' : 'Inactive'}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Display in Website
                                                                </Typography>
                                                                <Typography
                                                                    variant="body1"
                                                                    color={warehouse.displayInWebsite ? 'success.main' : 'text.secondary'}
                                                                    sx={{ fontWeight: 600 }}
                                                                >
                                                                    {warehouse.displayInWebsite ? 'Shown as branch' : 'Hidden'}
                                                                </Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </CardContent>
                                                </Card>
                                            </Grid>

                                            {/* Address Information */}
                                            <Grid item xs={12} md={6}>
                                                <Card>
                                                    <CardContent>
                                                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <LocationIcon sx={{ mr: 1 }} />
                                                            Address Information
                                                        </Typography>
                                                        <Box sx={{ mt: 2 }}>
                                                            <Typography variant="body1" paragraph sx={{ fontWeight: 500 }}>
                                                                {warehouse.address.street}
                                                            </Typography>
                                                            <Typography variant="body1" paragraph>
                                                                {warehouse.address.city}, {warehouse.address.state} {warehouse.address.zipCode}
                                                            </Typography>
                                                            <Typography variant="body1">
                                                                {warehouse.address.country}
                                                            </Typography>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            </Grid>

                                            {/* Contact Information */}
                                            <Grid item xs={12} md={6}>
                                                <Card>
                                                    <CardContent>
                                                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <PersonIcon sx={{ mr: 1 }} />
                                                            Contact Information
                                                        </Typography>
                                                        <Box sx={{ mt: 2 }}>
                                                            <Typography variant="body1" paragraph>
                                                                <strong>Manager:</strong> {warehouse.contact.manager}
                                                            </Typography>
                                                            <Typography variant="body1" paragraph>
                                                                <strong>Phone:</strong> {warehouse.contact.phone || 'Not provided'}
                                                            </Typography>
                                                            <Typography variant="body1">
                                                                <strong>Email:</strong> {warehouse.contact.email || 'Not provided'}
                                                            </Typography>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            </Grid>

                                            {/* Capacity Information */}
                                            <Grid item xs={12} md={6}>
                                                <Card>
                                                    <CardContent>
                                                        <Typography variant="h6" gutterBottom>
                                                            Capacity Information
                                                        </Typography>
                                                        <Box sx={{ mt: 2 }}>
                                                            <Box sx={{ mb: 3 }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Total Capacity
                                                                </Typography>
                                                                <Typography variant="h6" color="primary">
                                                                    {warehouse.capacity.total.toLocaleString()} units
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ mb: 3 }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Used Capacity
                                                                </Typography>
                                                                <Typography variant="h6" color="warning.main">
                                                                    {warehouse.capacity.used.toLocaleString()} units
                                                                </Typography>
                                                            </Box>
                                                            <Box>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Available Capacity
                                                                </Typography>
                                                                <Typography variant="h6" color="success.main">
                                                                    {warehouse.capacity.available.toLocaleString()} units
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            </Grid>

                                            {/* Operating Hours */}
                                            <Grid item xs={12} md={6}>
                                                <Card>
                                                    <CardContent>
                                                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <ScheduleIcon sx={{ mr: 1 }} />
                                                            Operating Hours
                                                        </Typography>
                                                        <Box sx={{ mt: 2 }}>
                                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                                Timezone: {warehouse.operatingHours?.timezone || 'UTC'}
                                                            </Typography>
                                                            {WEEK_DAYS.map((day) => {
                                                                const dayHours = warehouse.operatingHours?.days?.[day];
                                                                const isOpen = dayHours
                                                                    ? dayHours.isOpen
                                                                    : true;
                                                                const openT = dayHours?.open || warehouse.operatingHours?.open || '09:00';
                                                                const closeT = dayHours?.close || warehouse.operatingHours?.close || '18:00';
                                                                return (
                                                                    <Box
                                                                        key={day}
                                                                        sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px dashed', borderColor: 'divider' }}
                                                                    >
                                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                            {dayLabels[day]}
                                                                        </Typography>
                                                                        <Typography variant="body2" color={isOpen ? 'text.primary' : 'text.secondary'}>
                                                                            {isOpen ? `${openT} - ${closeT}` : 'Closed'}
                                                                        </Typography>
                                                                    </Box>
                                                                );
                                                            })}
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            </Grid>

                                            {/* Additional Information */}
                                            <Grid item xs={12}>
                                                <Card>
                                                    <CardContent>
                                                        <Typography variant="h6" gutterBottom>
                                                            Additional Information
                                                        </Typography>
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Created Date
                                                                </Typography>
                                                                <Typography variant="body1">
                                                                    {warehouse.createdAt ? new Date(warehouse.createdAt).toLocaleDateString() : 'Unknown'}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Last Updated
                                                                </Typography>
                                                                <Typography variant="body1">
                                                                    {warehouse.updatedAt ? new Date(warehouse.updatedAt).toLocaleDateString() : 'Unknown'}
                                                                </Typography>
                                                            </Grid>
                                                            {warehouse.createdByName && (
                                                                <Grid item xs={12} sm={6}>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        Created By
                                                                    </Typography>
                                                                    <Typography variant="body1">
                                                                        {warehouse.createdByName}
                                                                    </Typography>
                                                                </Grid>
                                                            )}
                                                        </Grid>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        </Grid>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Drawer>

            {/* ── Full-screen saving overlay (mirrors EnhancedProductDrawer pattern) ── */}
            <Backdrop
                open={loading}
                sx={{ zIndex: (t) => t.zIndex.drawer + 200, flexDirection: 'column', gap: 3, bgcolor: 'rgba(0,0,0,0.6)' }}
            >
                <Box
                    sx={{
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                        px: 5,
                        py: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2.5,
                        minWidth: 280,
                        boxShadow: 24,
                    }}
                >
                    <CircularProgress size={48} thickness={4} />
                    <Box sx={{ width: '100%' }}>
                        <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}>
                            {mode === 'add' ? 'Creating Warehouse…' : 'Updating Warehouse…'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
                            Please wait, do not close this window
                        </Typography>
                    </Box>
                </Box>
            </Backdrop>
        </>
    );
};

export default WarehouseDrawer;